'use client';

import { ExpandButton } from "@/components/_rebrand/ExpandButton";
import { Text } from "@/components/_rebrand/Text";
import { TextCombination } from "@/components/_rebrand/TextCombination";
import { ThemeNameType } from "@/tailwind-config/theme.constants";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import ThemeWrapper from "../ThemeWrapper";
import { LinkedInButton } from "./LinkedInButton";

export type LeadershipCardProps = {
  title: string;
  description?: string;
  imageSrc: string;
  imageAlt: string;
  biography: string;
  linkedInUrl: string;
  theme?: ThemeNameType;
}

// Animtion variants for the expanding bio section
const BioBackgroundVariants = {
  initial: {
    clipPath: 'inset(8% 92% 92% 8% round 2px)',
  },
  animate: {
    clipPath: 'inset(0% 0% 0% 0% round 8px)',
  },
};

const BioTextVariants = {
  initial: {
    opacity: 0,
    y: 100,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
};

/**
 * The Leadership Card is a component tailored for the Leadership section of the about page.
 * 
 * The entire card is a link to the LinkedIn profile, but also contains a little expand button 
 * in the headshot that reveals the bio text.
 * 
 * One thing to note is the use of the Theme prop - the card needs it because the Expand Button's theme 
 * changes when the bio is expanded, so we need to know the parent theme to switch back and forth correctly. 
 */
export const LeadershipCard = ({ title, description, imageSrc, imageAlt, biography, linkedInUrl, theme }: LeadershipCardProps) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  return (
    <div className="relative">
      {/* The Expand Button lives in this sibling <div> rather than in the body of the <Link> card below, 
          otherwise we'd have a nested button situation where clicking the expand would also open the link. */}
      <div className="absolute top-3 left-3 z-1">
        {/* We need to disable the outer card's hover state when we hover on the expand button */}
        <div>
          {/* The Expand Button's theme is hard-coded to theme-dark-violet in the expanded state*/}
          <ThemeWrapper theme={isBioExpanded ? theme || "theme-dark-violet" : theme || "theme-white"}>
            <ExpandButton
              parentSurfaceClass="surface-l2"
              size="large"
              onClick={() => setIsBioExpanded(!isBioExpanded)}
              isExpanded={isBioExpanded} />
          </ThemeWrapper>
        </div>
      </div>
      <div className="surface-l1 rounded-12">
        <div className="relative ">
          {/* This is the expanding bio section, which unfurls when the Expand Button is clicked.
         It too is hard-coded to theme-dark-violet in the expanded state. */}
          <ThemeWrapper theme="theme-dark-violet">
            <div className="absolute p-2xs z-0 w-full h-full">
              {/* This div is animated with the clip-path property to reveal the bio text via 
             an expanding rounded rectangle which originates at the Expand Button's location*/}
              <motion.div
                className="surface-l0 overflow-hidden h-full w-full"
                initial="initial"
                animate={isBioExpanded ? "animate" : "initial"}
                variants={BioBackgroundVariants}
                transition={{
                  duration: 0.4,
                  ease: 'easeInOut',
                }}
              >
                {/* The text has a slight upward motion when it comes into view */}
                <div className="p-s absolute overflow-hidden">
                  <motion.div
                    className="pt-2xl"
                    initial="initial"
                    animate={isBioExpanded ? "animate" : "initial"}
                    variants={BioTextVariants}
                    transition={{
                      duration: 0.4,
                      ease: 'easeInOut',
                    }}
                  >
                    <Text type="p1-medium" className="text-primary">
                      {biography}
                    </Text>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </ThemeWrapper>
          <div className="p-2xs">
            <Image className="w-full overflow-hidden rounded-8" src={imageSrc} alt={imageAlt} width={400} height={400} />
          </div>
        </div>
        <div className="px-xl pt-lg pb-xl">
          <TextCombination type="h3p2" align="left" headingLevel="h3" title={title} description={description} />
          <div className="mt-md flex justify-end">
            <Link href={linkedInUrl} target="_blank">
              <LinkedInButton url={linkedInUrl} />
            </Link>
          </div>
        </div>
      </div>
    </div >
  );
};
